import { useEffect, useState, useCallback } from "react";
import { Plus, Pencil, Trash2, Search, Download, Upload } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, DAYS, todayISO } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { WorkerDialog } from "../components/WorkerDialog";
import { ImportDialog } from "../components/ImportDialog";
import { StatusBadge } from "../components/StatusBadge";

const empStatus = (w) => (w.employment_status && w.employment_status !== "active" ? w.employment_status : w.active ? "active" : "inactive");
const TABS = [["all", "All"], ["active", "Active"], ["escaped", "Escaped"], ["terminated", "Terminated"], ["inactive", "Inactive"]];

const Expiry = ({ value, testId }) => {
  if (!value) return <span className="text-slate-300" data-testid={testId}>—</span>;
  const days = Math.round((new Date(value) - new Date(todayISO())) / 86400000);
  const [cls, note] = days < 0 ? ["bg-rose-50 text-rose-700 border-rose-200", "Expired"] : days <= 30 ? ["bg-amber-50 text-amber-700 border-amber-200", `${days}d left`] : ["bg-slate-50 text-slate-600 border-slate-200", ""];
  return (
    <span data-testid={testId} className={`inline-flex flex-col px-2 py-0.5 rounded-md border font-mono text-xs ${cls}`}>
      {value}{note && <span className="text-[10px] font-bold uppercase">{note}</span>}
    </span>
  );
};

export default function Workers() {
  const [workers, setWorkers] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [tab, setTab] = useState("all");

  const load = useCallback(() => api.get("/workers").then((r) => setWorkers(r.data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  const remove = async (w) => {
    if (!window.confirm(`Delete ${w.name}? Their past attendance records are kept.`)) return;
    try { await api.delete(`/workers/${w.id}`); toast.success("Worker deleted"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  const shown = workers.filter((w) => (tab === "all" || empStatus(w) === tab) && `${w.code} ${w.name} ${w.department} ${w.ram_pass_number || ""} ${w.iqama_number || ""} ${w.airport_prn || ""}`.toLowerCase().includes(q.toLowerCase()));
  const count = (t) => (t === "all" ? workers.length : workers.filter((w) => empStatus(w) === t).length);

  const exportCsv = () => {
    const head = ["Code", "Name", "Department", "Shift Start", "Shift End", "Weekly Off", "Ram Pass No.", "Ram Pass Expiry", "Iqama No.", "Iqama Expiry", "Airport PRN", "Status", "Status Date"];
    const rows = workers.map((w) => [w.code, w.name, w.department, w.shift_start, w.shift_end, DAYS[w.weekly_off], w.ram_pass_number, w.ram_pass_expiry, w.iqama_number, w.iqama_expiry, w.airport_prn, empStatus(w).replace(/^./, (c) => c.toUpperCase()), w.status_date]);
    const csv = [head, ...rows].map((l) => l.map((v) => `"${String(v ?? "").replace(/"/g, '""')}"`).join(",")).join("\r\n");
    const a = document.createElement("a");
    a.href = URL.createObjectURL(new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8" }));
    a.download = `workers_${todayISO()}.csv`;
    a.click();
  };

  return (
    <>
      <PageHeader overline={`${workers.length} registered`} title="Workers">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search code, name, Iqama, PRN" data-testid="worker-search-input" className="h-10 pl-9 pr-3 rounded-full border border-slate-300 bg-white text-sm outline-none focus:border-slate-900 w-60" />
        </div>
        <button onClick={() => setImportOpen(true)} data-testid="workers-import-btn" className="h-10 px-5 rounded-full border border-slate-300 bg-white hover:bg-slate-900 hover:text-white text-sm font-semibold flex items-center gap-2 active:scale-[0.98] transition-colors">
          <Upload size={16} /> Import Excel/CSV
        </button>
        <button onClick={exportCsv} disabled={!workers.length} data-testid="workers-csv-export-btn" className="h-10 px-5 rounded-full bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 text-slate-950 text-sm font-semibold flex items-center gap-2 active:scale-[0.98] transition-colors">
          <Download size={16} /> Export Excel/CSV
        </button>
        <button onClick={() => { setEditing(null); setOpen(true); }} data-testid="worker-add-new-btn" className="h-10 px-5 rounded-full bg-slate-900 hover:bg-emerald-500 hover:text-slate-950 text-white text-sm font-semibold flex items-center gap-2 active:scale-[0.98] transition-colors">
          <Plus size={16} /> Add worker
        </button>
      </PageHeader>

      <div className="flex flex-wrap gap-2" data-testid="workers-status-tabs">
        {TABS.map(([t, l]) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`workers-tab-${t}`} className={`h-9 px-4 rounded-full text-sm font-medium border transition-colors ${tab === t ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:border-slate-900"}`}>
            {l} <span className="font-mono text-xs opacity-70">{count(t)}</span>
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase text-slate-500 font-mono bg-slate-50">
            <tr><th className="px-5 py-3">Code</th><th className="px-5 py-3">Name</th><th className="px-5 py-3">Department</th><th className="px-5 py-3">Shift</th><th className="px-5 py-3">Weekly off</th><th className="px-5 py-3">Ram Pass no.</th><th className="px-5 py-3">Ram Pass expiry</th><th className="px-5 py-3">Iqama no.</th><th className="px-5 py-3">Iqama expiry</th><th className="px-5 py-3">Airport PRN</th><th className="px-5 py-3">Status</th><th className="px-5 py-3" /></tr>
          </thead>
          <tbody>
            {shown.length === 0 && <tr><td colSpan={12} className="px-5 py-12 text-center text-slate-400">No workers yet. Add your first worker to start tracking.</td></tr>}
            {shown.map((w) => (
              <tr key={w.id} className="border-t border-slate-100 hover:bg-slate-50 transition-colors" data-testid={`worker-row-${w.id}`}>
                <td className="px-5 py-3 font-mono font-bold">{w.code}</td>
                <td className="px-5 py-3 font-medium text-slate-900">{w.name}</td>
                <td className="px-5 py-3 text-slate-600">{w.department || "—"}</td>
                <td className="px-5 py-3 font-mono">{w.shift_start}–{w.shift_end}</td>
                <td className="px-5 py-3"><span className="px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200 text-xs font-medium">{DAYS[w.weekly_off]}</span></td>
                <td className="px-5 py-3 font-mono text-xs" data-testid={`worker-ram-pass-number-${w.id}`}>{w.ram_pass_number || "—"}</td>
                <td className="px-5 py-3"><Expiry value={w.ram_pass_expiry} testId={`worker-ram-pass-expiry-${w.id}`} /></td>
                <td className="px-5 py-3 font-mono text-xs" data-testid={`worker-iqama-number-${w.id}`}>{w.iqama_number || "—"}</td>
                <td className="px-5 py-3"><Expiry value={w.iqama_expiry} testId={`worker-iqama-expiry-${w.id}`} /></td>
                <td className="px-5 py-3 font-mono text-xs" data-testid={`worker-airport-prn-${w.id}`}>{w.airport_prn || "—"}</td>
                <td className="px-5 py-3"><StatusBadge status={empStatus(w)} testId={`worker-status-${w.id}`} />{w.status_date && empStatus(w) !== "active" && <p className="font-mono text-[10px] text-slate-400 mt-1">{w.status_date}</p>}</td>
                <td className="px-5 py-3 text-right whitespace-nowrap">
                  <button onClick={() => { setEditing(w); setOpen(true); }} data-testid={`worker-edit-btn-${w.id}`} className="p-2 rounded-md hover:bg-slate-200 transition-colors"><Pencil size={14} /></button>
                  <button onClick={() => remove(w)} data-testid={`worker-delete-btn-${w.id}`} className="p-2 rounded-md hover:bg-rose-100 text-rose-600 transition-colors"><Trash2 size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <WorkerDialog open={open} onOpenChange={setOpen} worker={editing} onSaved={load} />
      <ImportDialog open={importOpen} onOpenChange={setImportOpen} onDone={load} />
    </>
  );
}
